import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { cx } from './core.tsx'
import { ConfirmDialog } from './overlay.tsx'
import { ativarRedimensionamentoDeTabela, ativarTabNaTabela } from './table-resize.ts'
import { ativarMoverTabela } from './table-move.ts'

/**
 * RichTextEditor — o editor rich text unico da plataforma (SPEC-20260807-1152).
 *
 * Origem: espelho do `RichTextEditor.dc.html` do workspace de design, evoluido
 * com os pedidos do produto. E O componente reutilizavel: toda tela que edita
 * texto rico usa este; mudou aqui, muda em todo lugar.
 *
 * Modos:
 *  - visualizar: HTML renderizado; duplo clique (ou gatilho externo) edita;
 *  - editar: moldura com toolbar completa (fonte, tamanho, formatacao, tabela,
 *    imagem com redimensionamento, revisao do salvo, limpar tudo);
 *  - expandido (estilo Google Docs): overlay com barra FIXA no topo (fora da
 *    pagina) com Salvar/Cancelar, regua fixa abaixo, folhas no formato
 *    escolhido (A4/Carta/Oficio) com borda sutil, paginacao imediata a cada
 *    tecla e a nova pagina aparece inteira.
 */

export type RichTextEditorProps = {
  value: string
  onSave: (html: string) => void
  placeholder?: string
  /** Abre em edicao quando vira true (na montagem ou depois). */
  startEditing?: boolean
  onAutoStart?: () => void
  /** Notifica o pai quando entra/sai do modo de edicao. */
  onEditingChange?: (editing: boolean) => void
  /** Oculta o botao "Editar" do modo visualizar (o pai fornece o gatilho). */
  hideEditButton?: boolean
  /**
   * Entrega o estado do seletor de paginas da VISUALIZACAO ao pai (null quando
   * nao ha paginacao). Quando informado, o seletor interno nao e renderizado —
   * o pai posiciona um <RtePager> onde quiser (ex.: no cabecalho da secao).
   */
  onPagerChange?: (state: RtePagerState | null) => void
  minHeight?: number
  /**
   * Sobe a imagem inserida e devolve a URL a usar no `<img src>`. Quando ausente,
   * o editor cai no base64 embutido (legado). Permite guardar a imagem fora do
   * HTML (ex.: R2) e deixar so a URL no conteudo.
   */
  onUploadImage?: (file: File) => Promise<string>
  /**
   * Pessoas oferecidas no menu "@" (mencoes, estilo Outline). Sem a lista, o
   * "@" e texto comum.
   */
  mentions?: readonly { id: string; name: string }[]
}

/** Estado do seletor de paginas exposto ao pai. */
export type RtePagerState = { page: number; pages: number; prev: () => void; next: () => void }

/** Seletor de paginas sutil, para alinhar junto ao titulo/lapis da secao. */
export function RtePager({ state }: { state: RtePagerState | null }) {
  if (!state) return null
  return (
    <span className="ob-rte__minipager" role="navigation" aria-label="Páginas da visualização">
      <button
        type="button"
        className="ob-rte__minipagerbtn"
        aria-label="Página anterior"
        title="Página anterior"
        disabled={state.page === 0}
        onClick={state.prev}
      >
        <i className="fas fa-chevron-left" aria-hidden />
      </button>
      <span className="ob-rte__minipagerinfo">
        {state.page + 1}/{state.pages}
      </span>
      <button
        type="button"
        className="ob-rte__minipagerbtn"
        aria-label="Próxima página"
        title="Próxima página"
        disabled={state.page >= state.pages - 1}
        onClick={state.next}
      >
        <i className="fas fa-chevron-right" aria-hidden />
      </button>
    </span>
  )
}

const TOOLS: readonly { key: string; label: string; icon: string }[] = [
  { key: 'bold', label: 'Negrito', icon: 'fas fa-bold' },
  { key: 'italic', label: 'Itálico', icon: 'fas fa-italic' },
  { key: 'underline', label: 'Sublinhado', icon: 'fas fa-underline' },
  { key: 'strikeThrough', label: 'Tachado', icon: 'fas fa-strikethrough' },
  { key: 'h', label: 'Título', icon: 'fas fa-heading' },
  { key: 'insertUnorderedList', label: 'Lista', icon: 'fas fa-list-ul' },
  { key: 'insertOrderedList', label: 'Lista numerada', icon: 'fas fa-list-ol' },
  { key: 'quote', label: 'Citação', icon: 'fas fa-quote-right' },
  { key: 'clear', label: 'Limpar formatação', icon: 'fas fa-remove-format' },
]

export const RTE_FONTS = ['Arial', 'Instrument Sans', 'Sora', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana'] as const
export const RTE_SIZES: readonly { value: string; label: string }[] = [
  { value: '1', label: '8' },
  { value: '2', label: '10' },
  { value: '3', label: '12' },
  { value: '4', label: '14' },
  { value: '5', label: '18' },
  { value: '6', label: '24' },
  { value: '7', label: '36' },
]

/**
 * Marcador do modo Documento, persistido como PREFIXO do proprio HTML salvo
 * (comentario: nao renderiza em lugar nenhum). Assim a escolha viaja com o
 * conteudo — a visualizacao e a proxima edicao ja abrem no modo certo.
 */
export const RTE_DOC_MARKER = '<!--rte-mode:doc-->'

// ---------------------------------------------------------------------------
// Leitura decorada (estilo Outline): sintaxe no codigo, titulos colapsaveis
// e ancoras. As decoracoes ganham a classe rte-deco e NUNCA sao salvas —
// limparDecoracaoRte reverte antes de qualquer serializacao.
// ---------------------------------------------------------------------------

const escaparTexto = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** Realce leve e generico (strings, comentarios, numeros, palavras-chave). */
function realcarCodigo(codigo: string) {
  const bruto = escaparTexto(codigo)
  const re =
    /(\/\/[^\n]*|--[^\n]*|#[^\n]*|\/\*[\s\S]*?\*\/)|("[^"\n]*"|&quot;(?:[^&]|&(?!quot;))*?&quot;|'[^'\n]*'|`[^`]*`)|\b(\d+(?:\.\d+)?)\b|\b(if|else|for|while|return|function|const|let|var|class|import|export|from|new|await|async|try|catch|throw|switch|case|break|continue|typeof|of|null|undefined|true|false|select|insert|update|delete|join|where|group|order|by|limit|values|into|def|elif|not|and|or|is|none|print|public|private|void|int|string|bool|interface|type|enum)\b/gi
  return bruto.replace(re, (m, com, str, num, kw) => {
    if (com) return `<span class="tok-com">${m}</span>`
    if (str) return `<span class="tok-str">${m}</span>`
    if (num) return `<span class="tok-num">${m}</span>`
    if (kw) return `<span class="tok-kw">${m}</span>`
    return m
  })
}

/** Recolhe/expande a secao de um titulo (ate o proximo de nivel <= o dele). */
function dobrarTitulo(h: HTMLElement) {
  const nivel = Number(h.tagName[1])
  const dobrado = h.classList.toggle('rte-dobrada')
  let alvo = h.nextElementSibling
  while (alvo) {
    if (/^H[1-3]$/.test(alvo.tagName) && Number(alvo.tagName[1]) <= nivel) break
    alvo.classList.toggle('rte-oculto', dobrado)
    alvo = alvo.nextElementSibling
  }
}

/** Decora um container de LEITURA de conteudo do editor (view, Docs, etc.). */
export function decorarLeituraRte(raiz: HTMLElement) {
  // codigo com realce (preserva o botao copiar)
  for (const pre of raiz.querySelectorAll('pre.rte-code')) {
    if (pre.querySelector('.rte-hl')) continue
    const botao = pre.querySelector('.rte-copy')
    const texto = [...pre.childNodes]
      .filter((x) => x !== botao)
      .map((x) => x.textContent ?? '')
      .join('')
    for (const x of [...pre.childNodes]) if (x !== botao) x.remove()
    const span = document.createElement('span')
    span.className = 'rte-hl rte-deco-hl'
    span.innerHTML = realcarCodigo(texto)
    pre.append(span)
  }
  // titulos: id (ancora), botao de copiar link e colapso
  let seq = 0
  for (const h of raiz.querySelectorAll('h1, h2, h3')) {
    const el = h as HTMLElement
    if (el.querySelector('.rte-hfold')) continue
    seq += 1
    if (!el.id) {
      const slug = (el.textContent ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
      el.id = `h-${slug || 'titulo'}-${seq}`
    }
    const dobra = document.createElement('button')
    dobra.type = 'button'
    dobra.className = 'rte-hfold rte-deco'
    dobra.title = 'Recolher/expandir esta seção'
    dobra.setAttribute('aria-label', 'Recolher/expandir esta seção')
    dobra.addEventListener('click', (e) => {
      e.stopPropagation()
      dobrarTitulo(el)
    })
    el.prepend(dobra)
    const anc = document.createElement('button')
    anc.type = 'button'
    anc.className = 'rte-hanchor rte-deco'
    anc.title = 'Copiar o link desta seção'
    anc.setAttribute('aria-label', 'Copiar o link desta seção')
    anc.addEventListener('click', (e) => {
      e.stopPropagation()
      void navigator.clipboard.writeText(`${location.origin}${location.pathname}#${el.id}`)
      anc.dataset.ok = '1'
      window.setTimeout(() => delete anc.dataset.ok, 1200)
    })
    el.append(anc)
  }
  // chegou com #ancora na URL: rola ate o titulo
  if (location.hash) raiz.querySelector(location.hash.replace(/[^#\w-]/g, ''))?.scrollIntoView({ block: 'start' })
}

/** Devolve o HTML LIMPO (sem decoracoes) de um container decorado. */
export function limparDecoracaoRte(raiz: HTMLElement) {
  const clone = raiz.cloneNode(true) as HTMLElement
  for (const b of clone.querySelectorAll('.rte-deco')) b.remove()
  for (const hl of clone.querySelectorAll('.rte-deco-hl')) hl.replaceWith(document.createTextNode(hl.textContent ?? ''))
  for (const esc of clone.querySelectorAll('.rte-oculto')) esc.classList.remove('rte-oculto')
  for (const dob of clone.querySelectorAll('.rte-dobrada')) dob.classList.remove('rte-dobrada')
  for (const el of clone.querySelectorAll('[class=""]')) el.removeAttribute('class')
  return clone.innerHTML
}

/** Dimensoes da folha em px @96dpi por formato. */
const PAGE_SIZE: Record<string, { w: number; h: number }> = {
  A4: { w: 794, h: 1123 },
  Carta: { w: 816, h: 1054 },
  'Ofício': { w: 816, h: 1247 },
}
const PAGE_PAD_TOP = 56
const PAGE_PAD_BOTTOM = 56
const PAGE_GAP = 28

// ---------------------------------------------------------------------------
// Selecao/redimensionamento de imagem dentro do contentEditable
// ---------------------------------------------------------------------------

type ImgRect = { top: number; left: number; width: number; height: number }

export function useRteImageTools(editRef: RefObject<HTMLDivElement | null>) {
  const [selected, setSelected] = useState<HTMLImageElement | null>(null)
  const [rect, setRect] = useState<ImgRect | null>(null)
  const drag = useRef<{ corner: 'nw' | 'ne' | 'sw' | 'se'; startX: number; startWidth: number } | null>(null)

  const readRect = (img: HTMLImageElement): ImgRect => {
    const r = img.getBoundingClientRect()
    return { top: r.top, left: r.left, width: r.width, height: r.height }
  }

  const clear = () => {
    if (selected) selected.style.outline = ''
    setSelected(null)
    setRect(null)
  }

  const onEditorClick = (event: ReactMouseEvent) => {
    const target = event.target
    if (target instanceof HTMLImageElement) {
      if (selected && selected !== target) selected.style.outline = ''
      target.style.outline = '2px solid #1B4DF5'
      setSelected(target)
      setRect(readRect(target))
    } else if (selected) {
      clear()
    }
  }

  // Arraste das alcas de canto: delta horizontal vira largura em px.
  useEffect(() => {
    if (!selected) return
    const onMove = (event: MouseEvent) => {
      if (!drag.current) return
      event.preventDefault()
      const { corner, startX, startWidth } = drag.current
      const dx = event.clientX - startX
      const delta = corner === 'ne' || corner === 'se' ? dx : -dx
      const maxWidth = editRef.current?.clientWidth ?? 2000
      const width = Math.max(40, Math.min(maxWidth, startWidth + delta))
      selected.style.width = `${Math.round(width)}px`
      selected.style.height = 'auto'
      setRect(readRect(selected))
    }
    const onUp = () => {
      if (!drag.current) return
      drag.current = null
      document.body.style.userSelect = ''
      editRef.current?.dispatchEvent(new Event('input', { bubbles: true }))
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [selected, editRef])

  // O menu e as alcas usam coordenadas de viewport capturadas no clique; o
  // canvas rola e o documento repagina SEM avisar — sem re-medir, eles ficam
  // orfaos no lugar antigo (bug 2026-09-02). Scroll/resize + intervalo curto
  // cobrem tambem os reflows silenciosos da paginacao.
  useEffect(() => {
    if (!selected) return
    const sync = () => {
      if (!selected.isConnected) {
        clear()
        return
      }
      const novo = readRect(selected)
      setRect((atual) =>
        atual && atual.top === novo.top && atual.left === novo.left && atual.width === novo.width && atual.height === novo.height
          ? atual
          : novo,
      )
    }
    document.addEventListener('scroll', sync, true)
    window.addEventListener('resize', sync)
    const iv = window.setInterval(sync, 250)
    return () => {
      document.removeEventListener('scroll', sync, true)
      window.removeEventListener('resize', sync)
      window.clearInterval(iv)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  // Com uma imagem selecionada, Delete (ou Backspace) a remove.
  useEffect(() => {
    if (!selected) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return
      event.preventDefault()
      selected.remove()
      clear()
      editRef.current?.dispatchEvent(new Event('input', { bubbles: true }))
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  // Some junto com o editor (save/cancel desmontam o contentEditable).
  useEffect(() => clear, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const applyWidth = (width: string) => {
    if (!selected) return
    selected.style.width = width
    selected.style.height = 'auto'
    setRect(readRect(selected))
    editRef.current?.dispatchEvent(new Event('input', { bubbles: true }))
  }

  const startDrag = (corner: 'nw' | 'ne' | 'sw' | 'se') => (event: ReactMouseEvent) => {
    if (!selected) return
    event.preventDefault()
    event.stopPropagation()
    drag.current = { corner, startX: event.clientX, startWidth: selected.getBoundingClientRect().width }
    document.body.style.userSelect = 'none'
  }

  const CORNERS: readonly { corner: 'nw' | 'ne' | 'sw' | 'se'; x: 'left' | 'right'; y: 'top' | 'bottom' }[] = [
    { corner: 'nw', x: 'left', y: 'top' },
    { corner: 'ne', x: 'right', y: 'top' },
    { corner: 'sw', x: 'left', y: 'bottom' },
    { corner: 'se', x: 'right', y: 'bottom' },
  ]

  const menu =
    selected && rect
      ? createPortal(
          <>
            <div
              className="ob-rte__imgmenu"
              role="toolbar"
              aria-label="Tamanho da imagem"
              style={{ top: Math.max(8, rect.top - 40), left: rect.left }}
            >
              {(['25%', '50%', '75%', '100%'] as const).map((width) => (
                <button key={width} type="button" className="ob-rte__imgbtn" title={`Largura ${width}`} onClick={() => applyWidth(width)}>
                  {width}
                </button>
              ))}
              <span className="ob-rte__sep" />
              <button
                type="button"
                className="ob-rte__imgbtn ob-rte__imgbtn--danger"
                title="Remover imagem"
                aria-label="Remover imagem"
                onClick={() => {
                  selected.remove()
                  clear()
                  editRef.current?.dispatchEvent(new Event('input', { bubbles: true }))
                }}
              >
                <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 10 }} />
              </button>
            </div>
            {CORNERS.map(({ corner, x, y }) => (
              <span
                key={corner}
                className="ob-rte__imghandle"
                title="Arraste para redimensionar"
                style={{
                  top: y === 'top' ? rect.top - 5 : rect.top + rect.height - 5,
                  left: x === 'left' ? rect.left - 5 : rect.left + rect.width - 5,
                  cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
                }}
                onMouseDown={startDrag(corner)}
              />
            ))}
          </>,
          document.body,
        )
      : null

  return { onEditorClick, imageMenu: menu, clearImageSelection: clear }
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function RichTextEditor({
  value,
  onSave,
  placeholder = 'Sem conteúdo — clique duas vezes para editar.',
  startEditing = false,
  onAutoStart,
  onEditingChange,
  hideEditButton = false,
  onPagerChange,
  minHeight = 140,
  onUploadImage,
  mentions = [],
}: RichTextEditorProps) {
  const [editing, setEditingRaw] = useState(false)
  const [showReview, setShowReview] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  const [expanded, setExpanded] = useState(false)
  // Modo Documento (paginas) vs editor simples — persistido no HTML salvo.
  const [docMode, setDocMode] = useState(() => value.startsWith(RTE_DOC_MARKER))
  /** HTML salvo sem o marcador de modo. */
  const cleanValue = value.startsWith(RTE_DOC_MARKER) ? value.slice(RTE_DOC_MARKER.length) : value
  const [pageFormat, setPageFormat] = useState('A4')
  const [zoom, setZoom] = useState('1')
  const [marginLeft, setMarginLeft] = useState(72)
  const [marginRight, setMarginRight] = useState(72)
  const [pageCount, setPageCount] = useState(1)
  // Visualizacao paginada: pagina atual e total (formato da pagina, sem bordas).
  const [viewPage, setViewPage] = useState(0)
  const [viewPages, setViewPages] = useState(1)

  const viewRef = useRef<HTMLDivElement>(null)
  const editRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLInputElement>(null)
  const reviewRef = useRef<HTMLDivElement>(null)
  const pageWrapRef = useRef<HTMLDivElement>(null)
  // Altura da caixa do editor inline, ajustavel pela borda inferior.
  const [inlineHeight, setInlineHeight] = useState(640)
  const draggingHeight = useRef(false)
  const inlineCanvasRef = useRef<HTMLDivElement>(null)
  const needSync = useRef(false)
  const pendingHtml = useRef<string | null>(null)
  const paginateRaf = useRef<number | null>(null)
  const draggingMargin = useRef<'left' | 'right' | null>(null)
  /**
   * Edicao estilo Outline (2026-09-06, SPEC-20260906-1215): menu "/" para
   * inserir blocos, atalhos markdown ao digitar e toolbar FLUTUANTE na
   * selecao. Tudo sobre o mesmo contentEditable — nada de editor novo.
   */
  const [slash, setSlash] = useState<{ x: number; y: number; filtro: string } | null>(null)
  const [slashIdx, setSlashIdx] = useState(0)
  const [bolha, setBolha] = useState<{ x: number; y: number } | null>(null)
  /** Paleta de cores do "Destacar" aberta dentro da bolha. */
  const [bolhaCores, setBolhaCores] = useState(false)
  /** Menu ":" de EMOJI e "@" de MENCAO — mesmos moldes do menu "/". */
  const [emojiMenu, setEmojiMenu] = useState<{ x: number; y: number; filtro: string } | null>(null)
  const [mentionMenu, setMentionMenu] = useState<{ x: number; y: number; filtro: string } | null>(null)

  const { onEditorClick, imageMenu, clearImageSelection } = useRteImageTools(editRef)

  const scale = Number(zoom)
  const pageSize = PAGE_SIZE[pageFormat] ?? PAGE_SIZE.A4!
  const pageWidth = pageSize.w
  const pageHeight = pageSize.h

  const setEditing = (next: boolean) => {
    setEditingRaw(next)
    onEditingChange?.(next)
  }

  // Reage tanto na montagem quanto quando o pai liga `startEditing` depois.
  useEffect(() => {
    if (!startEditing) return
    if (!editing) {
      needSync.current = true
      setShowReview(false)
      setEditing(true)
    }
    // consome o pedido SEMPRE — se chegar com o editor ja aberto e nao
    // consumir, o autoEdit do pai fica travado em true e o lapis morre
    onAutoStart?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startEditing])

  // Area util da pagina (formato menos margens) — usada na edicao expandida e
  // na visualizacao paginada.
  const usableWidth = pageWidth - marginLeft - marginRight
  const usableHeight = pageHeight - PAGE_PAD_TOP - PAGE_PAD_BOTTOM

  /** HTML CRU aplicado por ultimo na view (o DOM decorado nao compara igual). */
  const viewHtmlRef = useRef<string | null>(null)

  // Sincroniza os contentEditable fora do fluxo do React.
  useEffect(() => {
    const view = viewRef.current
    if (view && !editing) {
      const html = cleanValue?.trim() ? cleanValue : `<p style="color:#6B76A3;font-size:13px">${placeholder}</p>`
      if (viewHtmlRef.current !== html) {
        viewHtmlRef.current = html
        view.innerHTML = html
        decorarLeituraRte(view)
      }
      // mede quantas "paginas" a visualizacao ocupa (so no modo Documento)
      const pages = docMode ? Math.max(1, Math.ceil(view.scrollHeight / usableHeight)) : 1
      if (pages !== viewPages) setViewPages(pages)
      if (viewPage > pages - 1) setViewPage(pages - 1)
    }
    const edit = editRef.current
    if (edit && editing && needSync.current) {
      edit.innerHTML = cleanValue || '<p><br></p>'
      needSync.current = false
      edit.focus()
      // pronto para digitar: cursor no INICIO da primeira linha (2026-09-02)
      const sel = window.getSelection()
      if (sel) {
        const range = document.createRange()
        range.selectNodeContents(edit)
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
      }
    }
  })

  // Conteudo novo salvo: volta para a primeira pagina e relê o modo gravado.
  useEffect(() => {
    setViewPage(0)
    setDocMode(value.startsWith(RTE_DOC_MARKER))
  }, [value])

  // Expoe o seletor de paginas ao pai (callback via ref para nao depender da
  // identidade da funcao; acoes estaveis com clamp pelo total corrente).
  const onPagerChangeRef = useRef(onPagerChange)
  onPagerChangeRef.current = onPagerChange
  const viewPagesRef = useRef(viewPages)
  viewPagesRef.current = viewPages
  const pagerActions = useRef({
    prev: () => setViewPage((p) => Math.max(0, p - 1)),
    next: () => setViewPage((p) => Math.min(viewPagesRef.current - 1, p + 1)),
  })
  useEffect(() => {
    if (!onPagerChangeRef.current) return
    onPagerChangeRef.current(
      editing || viewPages <= 1 ? null : { page: viewPage, pages: viewPages, ...pagerActions.current },
    )
  }, [viewPage, viewPages, editing])

  // Area de revisao mostra sempre o conteudo SALVO.
  useEffect(() => {
    const review = reviewRef.current
    if (review && showReview) review.innerHTML = cleanValue || '<p style="color:#6B76A3;font-size:13px">Sem conteúdo salvo.</p>'
  })

  // Arraste da borda inferior: define a altura da caixa do editor inline.
  // A borda e ancorada NA POSICAO DO MOUSE (nao num delta): mesmo que o scroll
  // da pagina se ajuste durante o arraste, a alca acompanha o cursor.
  useEffect(() => {
    if (!editing) return
    const onMove = (event: MouseEvent) => {
      if (!draggingHeight.current) return
      event.preventDefault()
      const canvasTop = inlineCanvasRef.current?.getBoundingClientRect().top
      if (canvasTop === undefined) return
      setInlineHeight(Math.max(240, Math.min(1600, event.clientY - canvasTop)))
    }
    const onUp = () => {
      if (!draggingHeight.current) return
      draggingHeight.current = false
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [editing])

  // Arraste dos marcadores de margem da regua (inline e expandido).
  useEffect(() => {
    if (!editing) return
    const onMove = (event: MouseEvent) => {
      if (!draggingMargin.current) return
      const page = pageWrapRef.current
      if (!page) return
      const rect = page.getBoundingClientRect()
      if (draggingMargin.current === 'left') {
        setMarginLeft(Math.max(24, Math.min(220, (event.clientX - rect.left) / scale)))
      } else {
        setMarginRight(Math.max(24, Math.min(220, (rect.right - event.clientX) / scale)))
      }
    }
    const onUp = () => {
      draggingMargin.current = null
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [expanded, scale])

  // ----------------------------------------------------------- paginacao ---

  const stripPageBreaks = (root: HTMLElement) => {
    for (const node of Array.from(root.querySelectorAll('.rte-pagebreak'))) node.remove()
  }

  const paginate = () => {
    const editorEl = editRef.current
    // paginacao so existe no modo Documento (inline ou expandido)
    if (!editorEl || !editing || !docMode) return
    stripPageBreaks(editorEl)

    const usableBottom = (page: number) => page * (pageHeight + PAGE_GAP) + pageHeight - PAGE_PAD_BOTTOM
    const contentTop = (page: number) => page * (pageHeight + PAGE_GAP) + PAGE_PAD_TOP
    let page = 0

    for (const child of Array.from(editorEl.children)) {
      const block = child as HTMLElement
      if (block.classList.contains('rte-pagebreak')) continue
      const top = block.offsetTop
      const bottom = top + block.offsetHeight
      while (top >= contentTop(page + 1) - PAGE_PAD_TOP) page += 1
      if (bottom > usableBottom(page) && top > contentTop(page)) {
        // espacador invisivel: empurra o bloco para o topo da proxima folha
        const spacer = document.createElement('div')
        spacer.className = 'rte-pagebreak'
        spacer.contentEditable = 'false'
        spacer.setAttribute('aria-hidden', 'true')
        spacer.style.cssText = `height:${contentTop(page + 1) - top}px;pointer-events:none;user-select:none`
        editorEl.insertBefore(spacer, block)
        page += 1
      }
    }
    setPageCount((current) => (current === page + 1 ? current : page + 1))
  }

  /**
   * Paginacao imediata e SINCRONA no proprio evento de digitacao — a quebra
   * acontece na primeira letra, como o Google Docs (rAF/timeout nao servem:
   * atrasariam um frame e nem rodam com a aba em segundo plano).
   */
  // Redimensionar colunas e linhas da tabela arrastando as bordas — o
  // contentEditable nao traz isso pronto.

  const schedulePaginate = () => {
    if (paginateRaf.current !== null) {
      cancelAnimationFrame(paginateRaf.current)
      paginateRaf.current = null
    }
    paginate()
  }

  // `schedulePaginate` muda a cada render; guardar em ref deixa o efeito
  // abaixo com dependencias estaveis.
  const paginarRef = useRef(schedulePaginate)
  paginarRef.current = schedulePaginate

  useEffect(() => {
    const alvo = editRef.current
    if (!alvo) return
    const paginar = () => paginarRef.current()
    const soltarResize = ativarRedimensionamentoDeTabela(alvo, paginar)
    const soltarTab = ativarTabNaTabela(alvo, paginar)
    const soltarMover = ativarMoverTabela(alvo, paginar)
    return () => {
      soltarResize()
      soltarTab()
      soltarMover()
    }
    // Religa so quando troca o elemento editavel (modo pagina <-> normal).
    // Sem isto o efeito rodava a cada render e removia os listeners no meio
    // do arraste — no modo pagina, que repagina o tempo todo, era constante.
  }, [editing])

  /** Alterna o expandido preservando o conteudo entre as estruturas. */
  const toggleExpanded = () => {
    pendingHtml.current = editRef.current?.innerHTML ?? null
    setExpanded((v) => !v)
  }

  /** Liga/desliga o modo Documento preservando o conteudo. */
  const toggleDocMode = () => {
    const html = editRef.current?.innerHTML ?? null
    if (html !== null) {
      const scratch = document.createElement('div')
      scratch.innerHTML = html
      stripPageBreaks(scratch)
      pendingHtml.current = scratch.innerHTML
    }
    setDocMode((v) => {
      if (v) setExpanded(false) // sair do doc tambem sai do expandido
      return !v
    })
  }

  // Depois de trocar de estrutura, restaura o conteudo e (re)pagina.
  useEffect(() => {
    const editorEl = editRef.current
    if (!editorEl) return
    if (pendingHtml.current !== null) {
      editorEl.innerHTML = pendingHtml.current
      pendingHtml.current = null
      editorEl.focus()
    }
    if (editing && docMode) paginate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, pageFormat, zoom, marginLeft, marginRight, editing, docMode])

  // ------------------------------------------------------------- comandos ---

  const exec = (cmd: string, val?: string) => {
    editRef.current?.focus()
    document.execCommand('styleWithCSS', false, String(cmd === 'fontName'))
    document.execCommand(cmd, false, val ?? undefined)
  }

  const runTool = (key: string) => {
    if (key === 'h') exec('formatBlock', 'H3')
    else if (key === 'quote') exec('formatBlock', 'BLOCKQUOTE')
    else if (key === 'clear') {
      exec('removeFormat')
      exec('formatBlock', 'P')
    } else exec(key)
  }

  // ------------------------------------------------- edicao estilo Outline ---

  /** Bloco (filho direto do editavel) onde o cursor esta. */
  const blocoAtual = (): HTMLElement | null => {
    const sel = window.getSelection()
    const ed = editRef.current
    if (!sel || !ed || sel.rangeCount === 0) return null
    let node: Node | null = sel.anchorNode
    if (!node || !ed.contains(node)) return null
    if (node === ed) return (ed.children[Math.min(sel.anchorOffset, ed.children.length - 1)] as HTMLElement) ?? null
    while (node && node.parentNode !== ed) node = node.parentNode
    return node instanceof HTMLElement ? node : null
  }

  const colocarCaretEm = (el: Element | null, inicio = false) => {
    if (!el) return
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(inicio)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  const semAcento = (t: string) => t.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

  /** Blocos do menu "/" (e dos atalhos markdown). */
  const BLOCOS_SLASH: readonly { key: string; rotulo: string; icone: string; dica: string }[] = [
    { key: 'h1', rotulo: 'Título 1', icone: 'fas fa-heading', dica: '# ' },
    { key: 'h2', rotulo: 'Título 2', icone: 'fas fa-heading', dica: '## ' },
    { key: 'h3', rotulo: 'Título 3', icone: 'fas fa-heading', dica: '### ' },
    { key: 'lista', rotulo: 'Lista', icone: 'fas fa-list-ul', dica: '- ' },
    { key: 'numerada', rotulo: 'Lista numerada', icone: 'fas fa-list-ol', dica: '1. ' },
    { key: 'tarefas', rotulo: 'Lista de tarefas', icone: 'far fa-check-square', dica: '[] ' },
    { key: 'citacao', rotulo: 'Citação', icone: 'fas fa-quote-right', dica: '> ' },
    { key: 'info', rotulo: 'Informação', icone: 'fas fa-info-circle', dica: 'bloco azul' },
    { key: 'aviso', rotulo: 'Aviso', icone: 'fas fa-exclamation-triangle', dica: 'bloco amarelo' },
    { key: 'ok', rotulo: 'Sucesso', icone: 'fas fa-check-circle', dica: 'bloco verde' },
    { key: 'codigo', rotulo: 'Bloco de código', icone: 'fas fa-code', dica: '```' },
    { key: 'tabela', rotulo: 'Tabela', icone: 'fas fa-table', dica: '3 colunas' },
    { key: 'divisor', rotulo: 'Divisor', icone: 'fas fa-minus', dica: '---' },
    { key: 'imagem', rotulo: 'Imagem', icone: 'far fa-image', dica: 'enviar arquivo' },
  ]
  const opcoesSlash = slash
    ? BLOCOS_SLASH.filter((b) => semAcento(b.rotulo).includes(semAcento(slash.filtro)))
    : []

  /** Digitou no editor: o bloco que comeca com "/" abre (e filtra) o menu. */
  const atualizarSlash = () => {
    const bloco = blocoAtual()
    const texto = bloco?.textContent ?? ''
    const sel = window.getSelection()
    if (bloco && texto.startsWith('/') && texto.length <= 24 && !texto.includes(' ') && sel?.rangeCount) {
      const r = sel.getRangeAt(0).getBoundingClientRect()
      setSlash({ x: Math.min(r.left, window.innerWidth - 300), y: Math.min(r.bottom + 6, window.innerHeight - 340), filtro: texto.slice(1) })
      setSlashIdx(0)
    } else if (slash) setSlash(null)
  }

  const criarTodoLi = () => {
    const li = document.createElement('li')
    const cb = document.createElement('input')
    cb.type = 'checkbox'
    cb.contentEditable = 'false'
    li.append(cb, document.createTextNode('\u00A0'))
    return li
  }

  const avisarMudanca = () => editRef.current?.dispatchEvent(new Event('input', { bubbles: true }))

  /** Aplica um bloco do menu "/" (ou de um atalho markdown). */
  const aplicarBloco = (key: string) => {
    const ed = editRef.current
    if (!ed) return
    setSlash(null)
    const bloco = blocoAtual()
    if (bloco) {
      bloco.innerHTML = '<br>'
      colocarCaretEm(bloco)
    }
    switch (key) {
      case 'h1':
      case 'h2':
      case 'h3':
        exec('formatBlock', key.toUpperCase())
        break
      case 'lista':
        exec('insertUnorderedList')
        break
      case 'numerada':
        exec('insertOrderedList')
        break
      case 'tarefas': {
        if (!bloco) break
        const ul = document.createElement('ul')
        ul.className = 'rte-todo'
        const li = criarTodoLi()
        ul.append(li)
        bloco.replaceWith(ul)
        colocarCaretEm(li)
        break
      }
      case 'citacao':
        exec('formatBlock', 'BLOCKQUOTE')
        break
      case 'codigo': {
        exec('formatBlock', 'PRE')
        const pre = blocoAtual()
        pre?.classList.add('rte-code')
        if (pre && !pre.querySelector('.rte-copy')) {
          const btn = document.createElement('button')
          btn.type = 'button'
          btn.className = 'rte-copy'
          btn.contentEditable = 'false'
          btn.title = 'Copiar o código'
          btn.textContent = 'copiar'
          pre.prepend(btn)
        }
        break
      }
      case 'divisor': {
        if (!bloco) break
        const hr = document.createElement('hr')
        const par = document.createElement('p')
        par.innerHTML = '<br>'
        bloco.replaceWith(hr, par)
        colocarCaretEm(par)
        break
      }
      case 'info':
      case 'aviso':
      case 'ok': {
        if (!bloco) break
        const dv = document.createElement('div')
        dv.className = `rte-callout rte-callout--${key}`
        dv.innerHTML = '<p><br></p>'
        bloco.replaceWith(dv)
        colocarCaretEm(dv.querySelector('p'))
        break
      }
      case 'tabela':
        insertTable()
        break
      case 'imagem':
        imgRef.current?.click()
        break
    }
    avisarMudanca()
  }

  // ------------------------------------------ emoji ":" e mencoes "@" ---

  /** Texto do NO DE TEXTO atual ate o cursor (para detectar :emoji e @nome). */
  const textoAteOCursor = () => {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return null
    const node = sel.anchorNode
    if (!node || node.nodeType !== Node.TEXT_NODE || !editRef.current?.contains(node)) return null
    return { node: node as Text, offset: sel.anchorOffset, texto: (node.textContent ?? '').slice(0, sel.anchorOffset) }
  }

  const atualizarMenusInline = () => {
    const info = textoAteOCursor()
    const sel = window.getSelection()
    if (!info || !sel?.rangeCount) {
      setEmojiMenu(null)
      setMentionMenu(null)
      return
    }
    const rect = sel.getRangeAt(0).getBoundingClientRect()
    const pos = { x: Math.min(rect.left, window.innerWidth - 300), y: Math.min(rect.bottom + 6, window.innerHeight - 300) }
    const emoji = /:([a-zA-Z]{2,16})$/.exec(info.texto)
    const mencao = mentions.length > 0 ? /@([\p{L}\p{N} ]{0,24})$/u.exec(info.texto) : null
    if (emoji) {
      setEmojiMenu({ ...pos, filtro: emoji[1]!.toLowerCase() })
      setMentionMenu(null)
      setSlashIdx(0)
    } else if (mencao) {
      setMentionMenu({ ...pos, filtro: mencao[1]! })
      setEmojiMenu(null)
      setSlashIdx(0)
    } else {
      setEmojiMenu(null)
      setMentionMenu(null)
    }
  }

  /** Apaga o gatilho digitado (":sor" / "@ana") e insere o no da escolha. */
  const substituirGatilho = (tamanho: number, no: Node | string) => {
    const info = textoAteOCursor()
    if (!info) return
    const range = document.createRange()
    range.setStart(info.node, info.offset - tamanho)
    range.setEnd(info.node, info.offset)
    range.deleteContents()
    const inserir = typeof no === 'string' ? document.createTextNode(no) : no
    range.insertNode(inserir)
    range.setStartAfter(inserir)
    range.collapse(true)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
    avisarMudanca()
  }

  const EMOJIS: readonly { nome: string; char: string }[] = [
    { nome: 'sorriso', char: '😄' }, { nome: 'feliz', char: '😊' }, { nome: 'risada', char: '😂' },
    { nome: 'piscada', char: '😉' }, { nome: 'amor', char: '😍' }, { nome: 'coracao', char: '❤️' },
    { nome: 'joinha', char: '👍' }, { nome: 'negativo', char: '👎' }, { nome: 'palmas', char: '👏' },
    { nome: 'forca', char: '💪' }, { nome: 'maos', char: '🙌' }, { nome: 'ok', char: '👌' },
    { nome: 'fogo', char: '🔥' }, { nome: 'estrela', char: '⭐' }, { nome: 'brilho', char: '✨' },
    { nome: 'festa', char: '🎉' }, { nome: 'foguete', char: '🚀' }, { nome: 'check', char: '✅' },
    { nome: 'xis', char: '❌' }, { nome: 'atencao', char: '⚠️' }, { nome: 'info', char: 'ℹ️' },
    { nome: 'ideia', char: '💡' }, { nome: 'alvo', char: '🎯' }, { nome: 'olhos', char: '👀' },
    { nome: 'pensando', char: '🤔' }, { nome: 'triste', char: '😢' }, { nome: 'bravo', char: '😠' },
    { nome: 'calendario', char: '📅' }, { nome: 'relogio', char: '⏰' }, { nome: 'prazo', char: '⏳' },
    { nome: 'nota', char: '📝' }, { nome: 'livro', char: '📚' }, { nome: 'pasta', char: '📁' },
    { nome: 'grafico', char: '📈' }, { nome: 'queda', char: '📉' }, { nome: 'dinheiro', char: '💰' },
    { nome: 'email', char: '📧' }, { nome: 'telefone', char: '📞' }, { nome: 'link', char: '🔗' },
    { nome: 'cadeado', char: '🔒' }, { nome: 'chave', char: '🔑' }, { nome: 'ferramenta', char: '🔧' },
    { nome: 'bug', char: '🐛' }, { nome: 'computador', char: '💻' }, { nome: 'celular', char: '📱' },
    { nome: 'casa', char: '🏠' }, { nome: 'predio', char: '🏢' }, { nome: 'carro', char: '🚗' },
    { nome: 'aviao', char: '✈️' }, { nome: 'mundo', char: '🌎' }, { nome: 'sol', char: '☀️' },
    { nome: 'chuva', char: '🌧️' }, { nome: 'cafe', char: '☕' }, { nome: 'pizza', char: '🍕' },
    { nome: 'bolo', char: '🎂' }, { nome: 'presente', char: '🎁' }, { nome: 'trofeu', char: '🏆' },
    { nome: 'medalha', char: '🥇' }, { nome: 'bandeira', char: '🚩' }, { nome: 'pino', char: '📌' },
  ]
  const opcoesEmoji = emojiMenu
    ? EMOJIS.filter((e) => e.nome.includes(emojiMenu.filtro)).slice(0, 8)
    : []
  const opcoesMencao = mentionMenu
    ? mentions.filter((m) => semAcento(m.name).includes(semAcento(mentionMenu.filtro))).slice(0, 8)
    : []

  const aplicarEmoji = (char: string) => {
    if (!emojiMenu) return
    substituirGatilho(emojiMenu.filtro.length + 1, char)
    setEmojiMenu(null)
  }
  const aplicarMencao = (m: { id: string; name: string }) => {
    if (!mentionMenu) return
    const span = document.createElement('span')
    span.className = 'rte-mention'
    span.contentEditable = 'false'
    span.dataset.memberId = m.id
    span.textContent = `@${m.name}`
    substituirGatilho(mentionMenu.filtro.length + 1, span)
    setMentionMenu(null)
  }

  // ------------------------------------------------------ colar esperto ---

  const EMBED_YOUTUBE = /^https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,20})/
  const escapeHtmlRte = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  /** Markdown simples colado vira blocos (titulos, listas, citacao, hr). */
  const markdownParaHtml = (texto: string) => {
    const linhas = texto.split(/\r?\n/)
    const md = linhas.some((l) => /^(#{1,3} |[-*] |\d+\. |> |---$|\[[ x]?\] )/.test(l.trim()))
    if (!md || linhas.length < 2) return null
    let html = ''
    let lista: 'ul' | 'ol' | 'todo' | null = null
    const fecharLista = () => {
      if (lista === 'ul') html += '</ul>'
      if (lista === 'ol') html += '</ol>'
      if (lista === 'todo') html += '</ul>'
      lista = null
    }
    for (const bruta of linhas) {
      const l = bruta.trim()
      const inline = (t: string) =>
        escapeHtmlRte(t)
          .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*]+)\*/g, '<em>$1</em>')
          .replace(/`([^`]+)`/g, '<code>$1</code>')
      if (/^### /.test(l)) { fecharLista(); html += `<h3>${inline(l.slice(4))}</h3>` }
      else if (/^## /.test(l)) { fecharLista(); html += `<h2>${inline(l.slice(3))}</h2>` }
      else if (/^# /.test(l)) { fecharLista(); html += `<h1>${inline(l.slice(2))}</h1>` }
      else if (/^\[[ x]?\] /.test(l)) {
        if (lista !== 'todo') { fecharLista(); html += '<ul class="rte-todo">'; lista = 'todo' }
        html += `<li><input type="checkbox"${l[1] === 'x' ? ' checked' : ''}>\u00A0${inline(l.replace(/^\[[ x]?\] /, ''))}</li>`
      } else if (/^[-*] /.test(l)) {
        if (lista !== 'ul') { fecharLista(); html += '<ul>'; lista = 'ul' }
        html += `<li>${inline(l.slice(2))}</li>`
      } else if (/^\d+\. /.test(l)) {
        if (lista !== 'ol') { fecharLista(); html += '<ol>'; lista = 'ol' }
        html += `<li>${inline(l.replace(/^\d+\. /, ''))}</li>`
      } else if (/^> /.test(l)) { fecharLista(); html += `<blockquote>${inline(l.slice(2))}</blockquote>` }
      else if (l === '---') { fecharLista(); html += '<hr>' }
      else if (l === '') fecharLista()
      else { fecharLista(); html += `<p>${inline(l)}</p>` }
    }
    fecharLista()
    return html
  }

  /** Colar estilo Outline: URL sobre selecao vira LINK; YouTube vira embed;
   *  markdown vira blocos; o resto segue o colar padrao. */
  const aoColarNoEditor = (e: React.ClipboardEvent) => {
    const texto = e.clipboardData.getData('text/plain').trim()
    if (!texto) return
    const sel = window.getSelection()
    const ehUrl = /^https?:\/\/\S+$/.test(texto)
    if (ehUrl && sel && !sel.isCollapsed) {
      e.preventDefault()
      exec('createLink', texto)
      avisarMudanca()
      return
    }
    const yt = EMBED_YOUTUBE.exec(texto)
    if (yt) {
      e.preventDefault()
      exec(
        'insertHTML',
        `<div class="rte-embed" contenteditable="false"><iframe src="https://www.youtube.com/embed/${yt[1]}" allowfullscreen loading="lazy"></iframe></div><p><br></p>`,
      )
      avisarMudanca()
      return
    }
    if (ehUrl) {
      e.preventDefault()
      exec('insertHTML', `<a href="${texto}">${texto}</a>`)
      avisarMudanca()
      return
    }
    // so texto puro (sem HTML rico) e com cara de markdown
    if (!e.clipboardData.getData('text/html')) {
      const html = markdownParaHtml(texto)
      if (html) {
        e.preventDefault()
        exec('insertHTML', html)
        avisarMudanca()
      }
    }
  }

  /** Botao "copiar" dos blocos de codigo (edicao e leitura). */
  const copiarCodigo = (alvo: EventTarget | null) => {
    if (!(alvo instanceof HTMLElement) || !alvo.classList.contains('rte-copy')) return false
    const pre = alvo.closest('pre')
    if (pre) {
      void navigator.clipboard.writeText(pre.textContent ?? '')
      alvo.dataset.ok = '1'
      window.setTimeout(() => delete alvo.dataset.ok, 1200)
    }
    return true
  }

  /** Checkbox de tarefa: alterna e SERIALIZA no HTML (atributo checked). */
  const alternarCheckbox = (e: { target: EventTarget | null; preventDefault: () => void }) => {
    const alvo = e.target
    if (!(alvo instanceof HTMLInputElement) || alvo.type !== 'checkbox') return false
    e.preventDefault()
    const novo = !alvo.hasAttribute('checked')
    alvo.checked = novo
    if (novo) alvo.setAttribute('checked', '')
    else alvo.removeAttribute('checked')
    alvo.closest('li')?.classList.toggle('rte-feito', novo)
    return true
  }

  const aoClicarNoEditor = (e: ReactMouseEvent) => {
    if (copiarCodigo(e.target)) return
    if (alternarCheckbox(e)) {
      avisarMudanca()
      return
    }
    onEditorClick(e)
  }

  /** Na LEITURA a tarefa tambem marca — e o documento salva na hora. */
  const aoClicarNaLeitura = (e: ReactMouseEvent) => {
    if (copiarCodigo(e.target)) return
    // decoracoes (dobra/ancora) resolvem o proprio clique
    if (e.target instanceof HTMLElement && e.target.closest('.rte-deco')) return
    if (!alternarCheckbox(e)) return
    const html = viewRef.current ? limparDecoracaoRte(viewRef.current) : ''
    viewHtmlRef.current = null // forca re-render decorado apos o refetch
    onSave(docMode && html.trim() ? RTE_DOC_MARKER + html : html)
  }

  const aoDigitarNoEditor = () => {
    schedulePaginate()
    atualizarSlash()
    atualizarMenusInline()
  }

  const ATALHOS_MD: Record<string, string> = {
    '#': 'h1',
    '##': 'h2',
    '###': 'h3',
    '-': 'lista',
    '*': 'lista',
    '1.': 'numerada',
    '[]': 'tarefas',
    '>': 'citacao',
  }

  const aoTeclarNoEditor = (e: React.KeyboardEvent) => {
    // atalhos de teclado (estilo Outline)
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const k = e.key.toLowerCase()
      const atalho: Record<string, () => void> = {
        b: () => exec('bold'),
        i: () => exec('italic'),
        u: () => exec('underline'),
        e: () => codigoInline(),
        k: () => criarLink(),
      }
      if (e.shiftKey && k === 's') {
        e.preventDefault()
        exec('strikeThrough')
        avisarMudanca()
        return
      }
      if (e.shiftKey && k === 'h') {
        e.preventDefault()
        exec('hiliteColor', '#FFF59D')
        avisarMudanca()
        return
      }
      if (!e.shiftKey && atalho[k]) {
        e.preventDefault()
        atalho[k]!()
        avisarMudanca()
        return
      }
    }
    if (e.ctrlKey && e.altKey && ['1', '2', '3'].includes(e.key)) {
      e.preventDefault()
      exec('formatBlock', `H${e.key}`)
      avisarMudanca()
      return
    }
    // menus ":" (emoji) e "@" (mencao): mesmas teclas do menu "/"
    const menuInline = emojiMenu ? 'emoji' : mentionMenu ? 'mencao' : null
    if (menuInline) {
      const total = menuInline === 'emoji' ? opcoesEmoji.length : opcoesMencao.length
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && total > 0) {
        e.preventDefault()
        setSlashIdx((i) => (i + (e.key === 'ArrowDown' ? 1 : total - 1)) % total)
        return
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && total > 0) {
        e.preventDefault()
        const idx = Math.min(slashIdx, total - 1)
        if (menuInline === 'emoji') aplicarEmoji(opcoesEmoji[idx]!.char)
        else aplicarMencao(opcoesMencao[idx]!)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        setEmojiMenu(null)
        setMentionMenu(null)
        return
      }
    }
    // menu "/" aberto: setas navegam, Enter aplica, Esc fecha
    if (slash) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const total = opcoesSlash.length
        if (total > 0) setSlashIdx((i) => (i + (e.key === 'ArrowDown' ? 1 : total - 1)) % total)
        return
      }
      if (e.key === 'Enter' && opcoesSlash.length > 0) {
        e.preventDefault()
        aplicarBloco(opcoesSlash[Math.min(slashIdx, opcoesSlash.length - 1)]!.key)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        setSlash(null)
        return
      }
    }
    // atalhos markdown: "# ", "- ", "[] ", "> "...
    if (e.key === ' ') {
      const bloco = blocoAtual()
      const texto = (bloco?.textContent ?? '').replace(/\u00A0/g, ' ').trim()
      const key = ATALHOS_MD[texto]
      // so transforma bloco "simples" (P/DIV) — nao dentro de lista/callout
      if (key && bloco && (bloco.tagName === 'P' || bloco.tagName === 'DIV')) {
        e.preventDefault()
        aplicarBloco(key)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      const bloco = blocoAtual()
      const texto = (bloco?.textContent ?? '').trim()
      if (texto === '---' && bloco && bloco.tagName === 'P') {
        e.preventDefault()
        aplicarBloco('divisor')
        return
      }
      if (texto === '```' && bloco && bloco.tagName === 'P') {
        e.preventDefault()
        aplicarBloco('codigo')
        return
      }
      // lista de tarefas: Enter cria o proximo item COM checkbox; num item
      // vazio, sai da lista (comportamento do Outline)
      const sel = window.getSelection()
      const li = sel?.anchorNode instanceof Node ? (sel.anchorNode instanceof Element ? sel.anchorNode : sel.anchorNode.parentElement)?.closest('ul.rte-todo > li') : null
      if (li && editRef.current?.contains(li)) {
        e.preventDefault()
        const soCheckbox = (li.textContent ?? '').replace(/\u00A0/g, ' ').trim() === ''
        if (soCheckbox) {
          const ul = li.closest('ul')
          const par = document.createElement('p')
          par.innerHTML = '<br>'
          ul?.after(par)
          li.remove()
          if (ul && ul.children.length === 0) ul.remove()
          colocarCaretEm(par)
        } else {
          const novo = criarTodoLi()
          li.after(novo)
          colocarCaretEm(novo)
        }
        avisarMudanca()
        return
      }
    }
  }

  /** Envolve a selecao em <code> (nao existe execCommand para isso). */
  const codigoInline = () => {
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed) return
    const texto = sel.toString()
    exec('insertHTML', `<code>${texto.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</code>`)
  }

  const criarLink = () => {
    const url = window.prompt('Endereço do link (https://…):')
    if (!url) return
    exec('createLink', /^https?:\/\//.test(url) ? url : `https://${url}`)
  }

  // bolha de formatacao sobre a SELECAO (estilo Outline)
  useEffect(() => {
    if (!editing) {
      setBolha(null)
      return
    }
    const aoSelecionar = () => {
      const sel = window.getSelection()
      const ed = editRef.current
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !ed || !ed.contains(sel.anchorNode)) {
        setBolha(null)
        return
      }
      const r = sel.getRangeAt(0).getBoundingClientRect()
      if (r.width < 2) {
        setBolha(null)
        setBolhaCores(false)
        return
      }
      setBolha({ x: Math.min(Math.max(150, r.left + r.width / 2), window.innerWidth - 170), y: Math.max(46, r.top) })
    }
    document.addEventListener('selectionchange', aoSelecionar)
    return () => document.removeEventListener('selectionchange', aoSelecionar)
  }, [editing])

  const FERRAMENTAS_BOLHA: readonly { key: string; icone: string; rotulo: string }[] = [
    { key: 'bold', icone: 'fas fa-bold', rotulo: 'Negrito' },
    { key: 'italic', icone: 'fas fa-italic', rotulo: 'Itálico' },
    { key: 'underline', icone: 'fas fa-underline', rotulo: 'Sublinhado' },
    { key: 'strikeThrough', icone: 'fas fa-strikethrough', rotulo: 'Tachado' },
    { key: 'code', icone: 'fas fa-code', rotulo: 'Código' },
    { key: 'mark', icone: 'fas fa-highlighter', rotulo: 'Destacar' },
    { key: 'link', icone: 'fas fa-link', rotulo: 'Link' },
    { key: 'clear', icone: 'fas fa-remove-format', rotulo: 'Limpar' },
  ]
  const CORES_DESTAQUE = ['#FFF59D', '#B9F3C9', '#BBDDFF', '#FFC9C9', '#E6C9FF', '#FFD8A8'] as const
  const rodarBolha = (key: string) => {
    if (key === 'code') codigoInline()
    else if (key === 'mark') {
      setBolhaCores((v) => !v)
      return
    } else if (key === 'link') criarLink()
    else if (key === 'clear') {
      exec('removeFormat')
      exec('unlink')
    } else exec(key)
    avisarMudanca()
  }

  const menusOutline = editing
    ? createPortal(
        <>
          {bolha && !slash ? (
            <div
              className="ob-rte__bubble"
              role="toolbar"
              aria-label="Formatação da seleção"
              style={{ left: bolha.x, top: bolha.y }}
              onMouseDown={(e) => e.preventDefault()}
            >
              {FERRAMENTAS_BOLHA.map((f) => (
                <button key={f.key} type="button" title={f.rotulo} aria-label={f.rotulo} onClick={() => rodarBolha(f.key)}>
                  <i className={f.icone} aria-hidden />
                </button>
              ))}
              {bolhaCores ? (
                <span className="ob-rte__cores">
                  {CORES_DESTAQUE.map((cor) => (
                    <button
                      key={cor}
                      type="button"
                      title="Destacar nesta cor"
                      aria-label={`Destacar na cor ${cor}`}
                      style={{ background: cor }}
                      onClick={() => {
                        exec('hiliteColor', cor)
                        setBolhaCores(false)
                        avisarMudanca()
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    className="ob-rte__cores-tirar"
                    title="Tirar o destaque"
                    aria-label="Tirar o destaque"
                    onClick={() => {
                      exec('hiliteColor', 'transparent')
                      setBolhaCores(false)
                      avisarMudanca()
                    }}
                  >
                    <i className="fas fa-ban" aria-hidden />
                  </button>
                </span>
              ) : null}
            </div>
          ) : null}
          {emojiMenu && opcoesEmoji.length > 0 ? (
            <div className="ob-rte__slash ob-rte__slash--inline" role="menu" aria-label="Emoji" style={{ left: emojiMenu.x, top: emojiMenu.y }} onMouseDown={(e) => e.preventDefault()}>
              {opcoesEmoji.map((em, i) => (
                <button key={em.nome} type="button" role="menuitem" className={i === slashIdx ? 'ob-rte__slash-on' : ''} onClick={() => aplicarEmoji(em.char)} onMouseEnter={() => setSlashIdx(i)}>
                  <span className="ob-rte__emoji">{em.char}</span>
                  <span>:{em.nome}</span>
                </button>
              ))}
            </div>
          ) : null}
          {mentionMenu && opcoesMencao.length > 0 ? (
            <div className="ob-rte__slash ob-rte__slash--inline" role="menu" aria-label="Mencionar pessoa" style={{ left: mentionMenu.x, top: mentionMenu.y }} onMouseDown={(e) => e.preventDefault()}>
              {opcoesMencao.map((m, i) => (
                <button key={m.id} type="button" role="menuitem" className={i === slashIdx ? 'ob-rte__slash-on' : ''} onClick={() => aplicarMencao(m)} onMouseEnter={() => setSlashIdx(i)}>
                  <i className="fas fa-at" aria-hidden />
                  <span>{m.name}</span>
                </button>
              ))}
            </div>
          ) : null}
          {slash ? (
            <div className="ob-rte__slash" role="menu" aria-label="Inserir bloco" style={{ left: slash.x, top: slash.y }} onMouseDown={(e) => e.preventDefault()}>
              {opcoesSlash.length === 0 ? <span className="ob-rte__slash-vazio">Nada com “{slash.filtro}”</span> : null}
              {opcoesSlash.map((b, i) => (
                <button
                  key={b.key}
                  type="button"
                  role="menuitem"
                  className={i === slashIdx ? 'ob-rte__slash-on' : ''}
                  onClick={() => aplicarBloco(b.key)}
                  onMouseEnter={() => setSlashIdx(i)}
                >
                  <i className={b.icone} aria-hidden />
                  <span>{b.rotulo}</span>
                  <span className="ob-rte__slash-dica">{b.dica}</span>
                </button>
              ))}
            </div>
          ) : null}
        </>,
        document.body,
      )
    : null

  const insertTable = () => {
    const cell = '<td><br></td>'.repeat(3)
    exec(
      'insertHTML',
      `<table><tr><th>Coluna 1</th><th>Coluna 2</th><th>Coluna 3</th></tr>${`<tr>${cell}</tr>`.repeat(2)}</table><p><br></p>`,
    )
  }

  /** Adiciona uma linha na tabela onde o cursor esta. */
  const addTableRow = () => {
    const selection = window.getSelection()
    let node = selection?.anchorNode as HTMLElement | null
    while (node && node !== editRef.current) {
      if (node instanceof HTMLTableRowElement) {
        const clone = node.cloneNode(true) as HTMLTableRowElement
        for (const cell of Array.from(clone.cells)) cell.innerHTML = '<br>'
        node.after(clone)
        return
      }
      node = (node.parentElement ?? null) as HTMLElement | null
    }
    const tables = editRef.current?.getElementsByTagName('table')
    const last = tables?.[tables.length - 1]
    const lastRow = last?.rows[last.rows.length - 1]
    if (lastRow) {
      const clone = lastRow.cloneNode(true) as HTMLTableRowElement
      for (const cell of Array.from(clone.cells)) cell.innerHTML = '<br>'
      lastRow.after(clone)
    }
  }

  const startEdit = () => {
    needSync.current = true
    setShowReview(false)
    setEditing(true)
  }

  const save = () => {
    clearImageSelection()
    const clone = editRef.current?.cloneNode(true) as HTMLElement | undefined
    if (clone) stripPageBreaks(clone)
    const html = clone?.innerHTML ?? ''
    // o modo escolhido viaja junto com o conteudo (prefixo invisivel)
    onSave(docMode && html.trim() ? RTE_DOC_MARKER + html : html)
    setEditing(false)
    setExpanded(false)
    setShowReview(false)
  }

  const cancel = () => {
    clearImageSelection()
    setEditing(false)
    setExpanded(false)
    setShowReview(false)
  }

  // ------------------------------------------------------------- toolbar ---

  const toolbar = (
    <>
      <select
        aria-label="Fonte"
        title="Fonte"
        className="ob-rte__select"
        style={{ maxWidth: 130, height: 30, fontSize: 12.5 }}
        defaultValue="Arial"
        onChange={(e) => exec('fontName', e.target.value)}
      >
        {RTE_FONTS.map((font) => (
          <option key={font} value={font}>
            {font}
          </option>
        ))}
      </select>
      <select
        aria-label="Tamanho da fonte"
        title="Tamanho da fonte"
        className="ob-rte__select"
        style={{ height: 30, fontSize: 12.5 }}
        defaultValue="3"
        onChange={(e) => exec('fontSize', e.target.value)}
      >
        {RTE_SIZES.map((size) => (
          <option key={size.value} value={size.value}>
            {size.label}
          </option>
        ))}
      </select>
      <span className="ob-rte__sep" style={{ height: 20 }} />
      {TOOLS.map((tool) => (
        <button
          key={tool.key}
          type="button"
          className="ob-rte__tool"
          style={{ width: 32, height: 30 }}
          aria-label={tool.label}
          title={tool.label}
          onClick={() => runTool(tool.key)}
        >
          <i className={tool.icon} aria-hidden style={{ fontSize: 12 }} />
        </button>
      ))}
      <span className="ob-rte__sep" style={{ height: 20 }} />
      <button type="button" className="ob-rte__tool" style={{ width: 32, height: 30 }} aria-label="Inserir tabela" title="Inserir tabela 3×3" onClick={insertTable}>
        <i className="fas fa-table" aria-hidden style={{ fontSize: 12 }} />
      </button>
      <button
        type="button"
        className="ob-rte__tool"
        style={{ width: 32, height: 30 }}
        aria-label="Adicionar linha na tabela"
        title="Adicionar linha na tabela (com o cursor dentro dela)"
        onClick={addTableRow}
      >
        <i className="fas fa-plus" aria-hidden style={{ fontSize: 11 }} />
      </button>
      <button type="button" className="ob-rte__tool" style={{ width: 32, height: 30 }} aria-label="Inserir imagem" title="Inserir imagem no texto" onClick={() => imgRef.current?.click()}>
        <i className="far fa-image" aria-hidden style={{ fontSize: 12 }} />
      </button>
      <input
        ref={imgRef}
        type="file"
        accept="image/*"
        aria-hidden
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (!file) return
          // Com uploader (ex.: R2), guarda a imagem fora do HTML e insere so a
          // URL; sem ele, cai no base64 embutido de sempre.
          if (onUploadImage) {
            onUploadImage(file)
              .then((url) => exec('insertImage', url))
              .catch(() => {
                /* upload falhou: nao insere para nao embutir base64 gigante */
              })
            return
          }
          const reader = new FileReader()
          reader.onload = () => exec('insertImage', String(reader.result))
          reader.readAsDataURL(file)
        }}
      />
      <span className="ob-rte__sep" style={{ height: 20 }} />
      <button
        type="button"
        className={cx('ob-rte__tool', docMode && 'ob-rte__tool--on')}
        style={{ width: 32, height: 30 }}
        aria-label="Modo documento (com páginas)"
        aria-pressed={docMode}
        title={docMode ? 'Modo documento ATIVO: conteúdo em páginas. Clique para voltar ao editor simples.' : 'Ativar o modo documento: conteúdo em páginas no formato escolhido.'}
        onClick={toggleDocMode}
      >
        <i className="far fa-file-alt" aria-hidden style={{ fontSize: 12 }} />
      </button>
      {docMode ? (
        <>
          <select
            value={pageFormat}
            onChange={(e) => setPageFormat(e.target.value)}
            aria-label="Formato da página"
            title="Formato da página"
            className="ob-rte__select"
            style={{ height: 30, fontSize: 12.5 }}
          >
            <option value="A4">A4</option>
            <option value="Carta">Carta</option>
            <option value="Ofício">Ofício</option>
          </select>
          <select value={zoom} onChange={(e) => setZoom(e.target.value)} aria-label="Zoom" title="Zoom da página" className="ob-rte__select" style={{ height: 30, fontSize: 12.5 }}>
            <option value="0.75">75%</option>
            <option value="0.9">90%</option>
            <option value="1">100%</option>
            <option value="1.25">125%</option>
            <option value="1.5">150%</option>
          </select>
        </>
      ) : null}
      <span className="ob-rte__sep" style={{ height: 20 }} />
      <button
        type="button"
        className={cx('ob-rte__tool', showReview && 'ob-rte__tool--on')}
        style={{ width: 32, height: 30 }}
        aria-label="Revisão: comparar com o conteúdo salvo"
        aria-pressed={showReview}
        title="Revisão: mostrar/ocultar o conteúdo salvo"
        onClick={() => setShowReview((v) => !v)}
      >
        <i className="far fa-eye" aria-hidden style={{ fontSize: 12 }} />
      </button>
      <button
        type="button"
        className="ob-rte__tool"
        style={{ width: 32, height: 30 }}
        aria-label="Limpar todo o conteúdo"
        title="Limpar todo o conteúdo"
        onClick={() => setConfirmClear(true)}
      >
        <i className="fas fa-eraser" aria-hidden style={{ fontSize: 12 }} />
      </button>
      {docMode ? (
        <button
          type="button"
          className={cx('ob-rte__tool', expanded && 'ob-rte__tool--on')}
          style={{ width: 32, height: 30 }}
          aria-label={expanded ? 'Sair do modo expandido' : 'Modo expandido'}
          title={expanded ? 'Sair do modo expandido' : 'Modo expandido (tela cheia)'}
          onClick={toggleExpanded}
        >
          <i className={expanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'} aria-hidden style={{ fontSize: 12 }} />
        </button>
      ) : null}
    </>
  )

  const confirmDialog = (
    <ConfirmDialog
      open={confirmClear}
      title="Limpar todo o conteúdo?"
      description="Todo o texto, tabelas e imagens deste editor serão removidos. Você ainda pode Cancelar a edição para manter o que está salvo."
      confirmLabel="Limpar tudo"
      destructive
      onCancel={() => setConfirmClear(false)}
      onConfirm={() => {
        clearImageSelection()
        if (editRef.current) editRef.current.innerHTML = '<p><br></p>'
        editRef.current?.focus()
        setConfirmClear(false)
        schedulePaginate()
      }}
    />
  )

  const reviewArea = showReview ? (
    <div className="ob-rte__review desc-scroll" style={expanded ? { width: pageWidth * scale, margin: '16px auto 0', border: '1px solid var(--ob-border)', borderRadius: 9 } : undefined}>
      <div className="ob-rte__review-label">
        <i className="far fa-eye" aria-hidden />
        Sem alterações (salvo)
      </div>
      <div ref={reviewRef} className="ob-rte-body" aria-label="Conteúdo salvo, somente leitura" />
    </div>
  ) : null

  // ------------------------------------------------------------- renders ---

  // keys distintas nos modos: sem elas o React reutiliza o div da visualizacao
  // (com innerHTML fora do React) como container do editor.
  if (!editing) {
    const paginated = viewPages > 1
    // seletor interno so quando o pai NAO assumiu o pager (onPagerChange)
    const pager = paginated && !onPagerChange ? (
      <div className="ob-rte__pager" role="navigation" aria-label="Páginas da visualização" style={{ maxWidth: usableWidth }}>
        <button
          type="button"
          className="ob-rte__pagerbtn"
          aria-label="Página anterior"
          title="Página anterior"
          disabled={viewPage === 0}
          onClick={() => setViewPage((p) => Math.max(0, p - 1))}
        >
          <i className="fas fa-chevron-left" aria-hidden style={{ fontSize: 10 }} />
        </button>
        <span className="ob-rte__pagerinfo">
          Página {viewPage + 1} de {viewPages}
        </span>
        <button
          type="button"
          className="ob-rte__pagerbtn"
          aria-label="Próxima página"
          title="Próxima página"
          disabled={viewPage >= viewPages - 1}
          onClick={() => setViewPage((p) => Math.min(viewPages - 1, p + 1))}
        >
          <i className="fas fa-chevron-right" aria-hidden style={{ fontSize: 10 }} />
        </button>
      </div>
    ) : null

    return (
      <div key="view" className="ob-rte__viewrow">
        <div style={{ flex: 1, minWidth: 0 }}>
          {pager}
          {/* viewport no formato da pagina (sem bordas): mostra uma pagina por
              vez; o conteudo desliza conforme a pagina atual */}
          <div
            className="ob-rte__viewport"
            style={docMode ? { maxWidth: usableWidth, height: paginated ? usableHeight : undefined } : undefined}
            onClick={aoClicarNaLeitura}
            onDoubleClick={startEdit}
            title="Clique duas vezes para editar"
          >
            <div
              ref={viewRef}
              className="ob-rte-body ob-rte__view"
              style={paginated ? { transform: `translateY(-${viewPage * usableHeight}px)` } : undefined}
            />
          </div>
          {pager}
        </div>
        {hideEditButton ? null : (
          <button type="button" className="ob-rte__editbtn" onClick={startEdit}>
            Editar
          </button>
        )}
      </div>
    )
  }

  // regua horizontal (numeros a cada ~1cm) — fixa, fora da pagina; usada no
  // expandido e no inline (que e o expandido contido na caixa)
  const rulerNumbers = Array.from({ length: Math.floor(pageWidth / 37.8) }, (_, i) => i + 1)
  const rulerRow = (
    <div className="ob-rte__rulerrow">
      <div className="ob-rte__ruler" style={{ width: pageWidth * scale }}>
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: marginLeft * scale, background: 'var(--ob-background-3)' }} />
        <span style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: marginRight * scale, background: 'var(--ob-background-3)' }} />
        {rulerNumbers.map((n) => (
          <span key={n} style={{ position: 'absolute', left: n * 37.8 * scale, top: 4, fontSize: 8, color: 'var(--ob-text-muted)', transform: 'translateX(-50%)' }}>
            {n}
          </span>
        ))}
        <span
          title="Arraste para ajustar a margem esquerda"
          onMouseDown={() => (draggingMargin.current = 'left')}
          style={{ position: 'absolute', left: marginLeft * scale - 5, top: 5, width: 10, height: 12, cursor: 'ew-resize', background: 'var(--ob-primary)', clipPath: 'polygon(50% 0, 100% 40%, 100% 100%, 0 100%, 0 40%)' }}
        />
        <span
          title="Arraste para ajustar a margem direita"
          onMouseDown={() => (draggingMargin.current = 'right')}
          style={{ position: 'absolute', right: marginRight * scale - 5, top: 5, width: 10, height: 12, cursor: 'ew-resize', background: 'var(--ob-primary)', clipPath: 'polygon(50% 0, 100% 40%, 100% 100%, 0 100%, 0 40%)' }}
        />
      </div>
    </div>
  )

  if (expanded) {
    return createPortal(
      <div key="expanded" className="ob-rte__overlay">
        <div className="ob-rte__topbar">
          {toolbar}
          <span style={{ flex: 1 }} />
          <button type="button" className="ob-rte__save" style={{ height: 32 }} onClick={save}>
            Salvar
          </button>
          <button type="button" className="ob-rte__cancel" style={{ height: 32 }} onClick={cancel}>
            Cancelar
          </button>
        </div>

        {rulerRow}

        <div className="ob-rte__canvas desc-scroll">
          {reviewArea}
          <div style={{ width: pageWidth * scale, margin: '20px auto 90px' }}>
            <div
              ref={pageWrapRef}
              style={{
                position: 'relative',
                width: pageWidth,
                transform: scale !== 1 ? `scale(${scale})` : undefined,
                transformOrigin: 'top left',
              }}
            >
              {/* folhas atras do texto — borda sutil so ao redor da pagina */}
              <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                {Array.from({ length: pageCount }, (_, k) => (
                  <div key={k} className="ob-rte__sheet" style={{ top: k * (pageHeight + PAGE_GAP), height: pageHeight }} />
                ))}
              </div>
              <div
                ref={editRef}
                className="ob-rte-body"
                contentEditable
                role="textbox"
                aria-multiline="true"
                onClick={aoClicarNoEditor}
                onKeyDown={aoTeclarNoEditor}
                onInput={aoDigitarNoEditor}
                onPaste={aoColarNoEditor}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  background: 'transparent',
                  outline: 'none',
                  width: pageWidth,
                  minHeight: pageCount * pageHeight + (pageCount - 1) * PAGE_GAP,
                  padding: `${PAGE_PAD_TOP}px ${marginRight}px ${PAGE_PAD_BOTTOM}px ${marginLeft}px`,
                }}
              />
            </div>
          </div>
        </div>

        {imageMenu}
        {menusOutline}
        {confirmDialog}
      </div>,
      document.body,
    )
  }

  // Editor simples (sem paginacao): caixa unica de texto corrido.
  if (!docMode) {
    return (
      <div key="edit-plain">
        <div className="ob-rte__frame">
          {reviewArea}
          <div className="ob-rte__toolbar">{toolbar}</div>
          <div
            ref={editRef}
            className={cx('ob-rte-body', 'ob-rte__edit', 'desc-scroll')}
            contentEditable
            role="textbox"
            aria-multiline="true"
            style={{ minHeight }}
            onClick={aoClicarNoEditor}
            onKeyDown={aoTeclarNoEditor}
            onInput={aoDigitarNoEditor}
            onPaste={aoColarNoEditor}
          />
        </div>
        {imageMenu}
        {menusOutline}
        {confirmDialog}
        <div className="ob-rte__actions">
          <button type="button" className="ob-rte__save" onClick={save}>
            Salvar
          </button>
          <button type="button" className="ob-rte__cancel" onClick={cancel}>
            Cancelar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div key="edit">
      <div className="ob-rte__frame">
        {reviewArea}
        <div className="ob-rte__toolbar">{toolbar}</div>
        {/* o inline E o expandido contido na caixa: regua fixa + folhas do
            formato num canvas rolavel dentro da moldura */}
        <div style={{ background: 'var(--ob-background-3)', borderBottom: '1px solid var(--ob-border)' }}>{rulerRow}</div>
        <div ref={inlineCanvasRef} className="ob-rte__inlinecanvas desc-scroll" style={{ height: inlineHeight, maxHeight: 'none' }}>
          <div
            style={{
              width: pageWidth * scale,
              height: (pageCount * pageHeight + (pageCount - 1) * PAGE_GAP) * scale,
              margin: '0 auto',
              overflow: 'hidden',
            }}
          >
            <div
              ref={pageWrapRef}
              style={{
                position: 'relative',
                width: pageWidth,
                transform: scale !== 1 ? `scale(${scale})` : undefined,
                transformOrigin: 'top left',
              }}
            >
              <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                {Array.from({ length: pageCount }, (_, k) => (
                  <div key={k} className="ob-rte__sheet" style={{ top: k * (pageHeight + PAGE_GAP), height: pageHeight }} />
                ))}
              </div>
              <div
                ref={editRef}
                className="ob-rte-body"
                contentEditable
                role="textbox"
                aria-multiline="true"
                onClick={aoClicarNoEditor}
                onKeyDown={aoTeclarNoEditor}
                onInput={aoDigitarNoEditor}
                onPaste={aoColarNoEditor}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  background: 'transparent',
                  outline: 'none',
                  width: pageWidth,
                  minHeight: pageCount * pageHeight + (pageCount - 1) * PAGE_GAP,
                  padding: `${PAGE_PAD_TOP}px ${marginRight}px ${PAGE_PAD_BOTTOM}px ${marginLeft}px`,
                }}
              />
            </div>
          </div>
        </div>
        {/* borda inferior arrastavel: sobe/desce para definir a altura da caixa */}
        <div
          className="ob-rte__hresize"
          title="Arraste para ajustar a altura da caixa"
          onMouseDown={(e) => {
            e.preventDefault()
            draggingHeight.current = true
            document.body.style.userSelect = 'none'
          }}
        >
          <span className="ob-rte__hresize-grip" />
        </div>
      </div>
      {imageMenu}
      {menusOutline}
      {confirmDialog}
      <div className="ob-rte__actions">
        <button type="button" className="ob-rte__save" onClick={save}>
          Salvar
        </button>
        <button type="button" className="ob-rte__cancel" onClick={cancel}>
          Cancelar
        </button>
      </div>
    </div>
  )
}
